import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface SimpleToastProps {
  theme?: 'light' | 'dark';
}

let toastCounter = 0;
const toasts: Toast[] = [];
const listeners: ((toasts: Toast[]) => void)[] = [];

const notifyListeners = () => {
  // Используем setTimeout для асинхронного обновления состояния
  setTimeout(() => {
    listeners.forEach(listener => listener([...toasts]));
  }, 0);
};

export const toast = {
  success: (message: string) => {
    const newToast: Toast = {
      id: `toast-${++toastCounter}`,
      message,
      type: 'success'
    };
    toasts.push(newToast);
    notifyListeners();
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      const index = toasts.findIndex(t => t.id === newToast.id);
      if (index > -1) {
        toasts.splice(index, 1);
        notifyListeners();
      }
    }, 3000);
  },
  
  error: (message: string) => {
    const newToast: Toast = {
      id: `toast-${++toastCounter}`,
      message,
      type: 'error'
    };
    toasts.push(newToast);
    notifyListeners();
    
    // Auto remove after 4 seconds for errors
    setTimeout(() => {
      const index = toasts.findIndex(t => t.id === newToast.id);
      if (index > -1) {
        toasts.splice(index, 1);
        notifyListeners();
      }
    }, 4000);
  },
  
  info: (message: string) => {
    const newToast: Toast = {
      id: `toast-${++toastCounter}`,
      message,
      type: 'info'
    };
    toasts.push(newToast);
    notifyListeners();
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      const index = toasts.findIndex(t => t.id === newToast.id);
      if (index > -1) {
        toasts.splice(index, 1);
        notifyListeners();
      }
    }, 3000);
  }
};

export function SimpleToast({ theme = 'light' }: SimpleToastProps) {
  const [currentToasts, setCurrentToasts] = useState<Toast[]>([]);

  const updateToasts = useCallback((newToasts: Toast[]) => {
    setCurrentToasts(newToasts);
  }, []);

  useEffect(() => {
    listeners.push(updateToasts);
    
    return () => {
      const index = listeners.indexOf(updateToasts);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [updateToasts]);

  const removeToast = useCallback((id: string) => {
    const index = toasts.findIndex(t => t.id === id);
    if (index > -1) {
      toasts.splice(index, 1);
      notifyListeners();
    }
  }, []);

  const isDark = theme === 'dark';

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {currentToasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex items-center gap-3 p-4 rounded-lg shadow-lg border transform transition-all duration-300 ease-out animate-in slide-in-from-right-full ${
            isDark 
              ? 'bg-slate-800 border-slate-700 text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          } ${
            toast.type === 'success' 
              ? isDark 
                ? 'border-green-500/30' 
                : 'border-green-300'
              : toast.type === 'error'
              ? isDark
                ? 'border-red-500/30'
                : 'border-red-300'
              : isDark
              ? 'border-blue-500/30'
              : 'border-blue-300'
          }`}
        >
          {toast.type === 'success' && (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          )}
          {toast.type === 'error' && (
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          )}
          {toast.type === 'info' && (
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
          )}
          
          <span className="text-sm font-medium flex-1">{toast.message}</span>
          
          <button
            onClick={() => removeToast(toast.id)}
            className={`p-1 rounded-full transition-colors ${
              isDark 
                ? 'hover:bg-slate-700 text-slate-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
